<script setup lang="ts">
import { computed, ref, useId } from 'vue';
import DropdownArea from './DropdownArea.vue';
import { NxButton } from './../Button';

const emit = defineEmits<{ 'select-color': [color: string]; close: [] }>();
const selectedColor = ref<string>('#000000');
const selectedAlpha = ref<string>('255');
const endColor = computed(() => {
  return (
    selectedColor.value +
    (selectedAlpha.value == '255'
      ? ''
      : parseInt(selectedAlpha.value).toString(16).padStart(2, '0'))
  );
});

const uniqueId = useId();

// colors are from https://github.com/aesoper101/vue3-colorpicker/
const defaultColors: string[][] = [
  [
    '#fcc02e',
    '#f67c01',
    '#e64a19',
    '#d81b43',
    '#8e24aa',
    '#512da7',
    '#1f87e8',
    '#008781',
    '#05a045',
  ],
  [
    '#fed835',
    '#fb8c00',
    '#f5511e',
    '#eb1d4e',
    '#9c28b1',
    '#5d35b0',
    '#2097f3',
    '#029688',
    '#4cb050',
  ],
  [
    '#ffeb3c',
    '#ffa727',
    '#fe5722',
    '#eb4165',
    '#aa47bc',
    '#673bb7',
    '#42a5f6',
    '#26a59a',
    '#83c683',
  ],
  [
    '#fff176',
    '#ffb74e',
    '#ff8a66',
    '#f1627e',
    '#b968c7',
    '#7986cc',
    '#64b5f6',
    '#80cbc4',
    '#a5d6a7',
  ],
  [
    '#fff59c',
    '#ffcc80',
    '#ffab91',
    '#fb879e',
    '#cf93d9',
    '#9ea8db',
    '#90caf8',
    '#b2dfdc',
    '#c8e6ca',
  ],
  [
    'transparent',
    '#ffffff',
    '#dedede',
    '#a9a9a9',
    '#4b4b4b',
    '#353535',
    '#212121',
    '#000000',
    'picker',
  ],
];
</script>

<template>
  <DropdownArea
    @close="
      () => {
        selectedColor = '#000000';
        selectedAlpha = '255';
        emit('close');
      }
    "
  >
    <template #default>
      <slot></slot>
    </template>
    <template #area="{ close }">
      <div class="area">
        <div class="grid">
          <template v-for="(row, y) in defaultColors">
            <template v-for="(color, x) in row" :key="`color-${x}-${y}`">
              <template v-if="color === 'picker'">
                <label :for="`color-picker-${uniqueId}`" style="width: 100%; height: 100%">
                  <div class="box picker" style="width: 100%; height: 100%"></div>
                </label>
                <!-- hidden input -->
                <input
                  :id="`color-picker-${uniqueId}`"
                  v-model="selectedColor"
                  class="hidden-input"
                  type="color"
                  tabindex="-1"
                />
              </template>
              <div
                v-else
                :style="`--bg-color: ${color}`"
                :class="[
                  'box',
                  {
                    transparent: color === 'transparent',
                  },
                ]"
                @click="
                  () => {
                    selectedColor = color;
                    emit('select-color', endColor);
                    close();
                  }
                "
              ></div>
            </template>
          </template>
        </div>
        <div class="row">
          <div class="show transparent" :style="`--bg-color: ${endColor}`"></div>
          <input v-model="selectedAlpha" type="range" min="0" max="255" step="1" />
        </div>
        <NxButton
          width="full"
          @click="
            () => {
              emit('select-color', endColor);
              close();
            }
          "
        >
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
          {{ 'Ok' }}
        </NxButton>
      </div>
    </template>
  </DropdownArea>
</template>

<style scoped>
.area {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--nxt-space-xs);
}
.grid {
  display: grid;
  grid-template-columns: repeat(9, 1.5em);
  grid-template-rows: repeat(6, 1.5em);
}
.box {
  cursor: pointer;
  background-color: var(--bg-color);
  transition: transform var(--nxt-motion-base);

  &:hover {
    transform: scale(1.25);
    z-index: var(--nxt-z-above);
  }

  &.transparent::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      -45deg,
      transparent 0%,
      transparent 45%,
      var(--nxt-danger-bg) 50%,
      transparent 55%,
      transparent 100%
    );
  }
}

.transparent {
  position: relative;
  background-color: var(--nxt-surface-bg);
  opacity: 0.8;
  background-image:
    repeating-linear-gradient(
      45deg,
      var(--nxt-border) 25%,
      transparent 25%,
      transparent 75%,
      var(--nxt-border) 75%,
      var(--nxt-border)
    ),
    repeating-linear-gradient(
      45deg,
      var(--nxt-border) 25%,
      var(--nxt-surface-bg) 25%,
      var(--nxt-surface-bg) 75%,
      var(--nxt-border) 75%,
      var(--nxt-border)
    );
  background-position:
    0 0,
    0.25em 0.25em;
  background-size: 0.5em 0.5em;
}

.picker {
  position: relative;
  background: conic-gradient(
    hsl(360 100% 50%),
    hsl(315 100% 50%),
    hsl(270 100% 50%),
    hsl(225 100% 50%),
    hsl(180 100% 50%),
    hsl(135 100% 50%),
    hsl(90 100% 50%),
    hsl(45 100% 50%),
    hsl(0 100% 50%)
  );
}

.hidden-input {
  pointer-events: none;
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  bottom: 0;
  left: 0;
}

.row {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-lg);
}
.show {
  width: 1.75em;
  height: 1.75em;
  border-radius: 100%;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--bg-color);
  }
}
.alpha-slider {
  appearance: none;
  -webkit-appearance: none;
  outline: none;
  border-radius: var(--nxt-radius-md);
  height: 0.5em;
}
</style>
